// src/lib/products.js
import {
  collection,
  getDocs,
  doc,
  getDoc,
  addDoc,
  updateDoc,
  deleteDoc,
  query,
  orderBy,
  limit as qLimit,
  startAfter,
  serverTimestamp,
  where,
} from 'firebase/firestore'
import { db } from '../firebase'

// tout en haut du fichier
let _allProductsCache = null

const BATCH_SIZE = 300

// export async function fetchProductsAllCached() {
//   if (_allProductsCache) return _allProductsCache

//   const base = collection(db, 'productsShop')
//   let all = []
//   let last = null
//   let hasMore = true

//   while (hasMore) {
//     const q = last
//       ? query(base, orderBy('__name__'), startAfter(last), qLimit(BATCH_SIZE))
//       : query(base, orderBy('__name__'), qLimit(BATCH_SIZE))

//     const snap = await getDocs(q)
//     const docs = snap.docs.map(d => ({ id: d.id, ...normalizeProduct(d.data()) }))
//     all = all.concat(docs)

//     if (snap.docs.length < BATCH_SIZE) {
//       hasMore = false
//     } else {
//       last = snap.docs[snap.docs.length - 1]
//     }
//   }

//   _allProductsCache = all
//   return all
// }
export async function fetchProductsAllCached() {
  if (_allProductsCache) return _allProductsCache;

  const base = collection(db, "productsShop");
  let all = [];
  let last = null;
  let hasMore = true;

  while (hasMore) {
    const q = last
      ? query(
          base,
          where("validationStatus", "==", "validated"), // ✅
          orderBy("__name__"),
          startAfter(last),
          qLimit(BATCH_SIZE)
        )
      : query(
          base,
          where("validationStatus", "==", "validated"), // ✅
          orderBy("__name__"),
          qLimit(BATCH_SIZE)
        );

    const snap = await getDocs(q);

    const docs = snap.docs.map((d) => ({
      id: d.id,
      ...normalizeProduct(d.data()),
    }));

    all = all.concat(docs);

    if (snap.docs.length < BATCH_SIZE) {
      hasMore = false;
    } else {
      last = snap.docs[snap.docs.length - 1];
    }
  }

  _allProductsCache = all;
  return all;
}
/* ----------------- Utils SEO ----------------- */
// export function slugify(str = '') {
//   return String(str)
//     .normalize('NFD')
//     .replace(/[\u0300-\u036f]/g, '')
//     .toLowerCase()
//     .replace(/[^a-z0-9]+/g, '-')
//     .replace(/^-+|-+$/g, '')
// }
export function slugify(str = "") {
  return String(str)
    .normalize("NFD").replace(/[\u0300-\u036f]/g, "")
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "");
}


export function productUrl(p) {
  // On s'assure qu'il y a toujours un titre exploitable
  const title =
    p?.title ||
    p?.Name ||
    p?.name ||
    p?.Title ||
    'produit'
  const slug = slugify(title)
  const id = p?.id || p?.Id || ''
  // /ip/slug/id comme Walmart
  return `/ip/${slug}/${id}`
}

/* ----------------- Images ----------------- */
function toHttps(url) {
  if (typeof url !== 'string') return ''
  if (url.startsWith('http://')) return url.replace('http://', 'https://')
  return url
}

// Essaie d'extraire une URL d'image quel que soit le format des données
function pickImageUrl(p) {
  // 1) Champs directs
  if (typeof p.ImageUrl === 'string') return toHttps(p.ImageUrl)
  if (typeof p.imageUrl === 'string') return toHttps(p.imageUrl)
  if (typeof p.thumbnail === 'string') return toHttps(p.thumbnail)
  if (typeof p.image === 'string') return toHttps(p.image)

  // 2) Tableaux de strings
  const firstString = (arr) =>
    Array.isArray(arr) ? arr.find((x) => typeof x === 'string') : ''
  const s1 =
    firstString(p.ImageUrl) ||
    firstString(p.images) ||
    firstString(p.photos)
  if (s1) return toHttps(s1)

  // 3) Tableaux d'objets { url | Url | src | link }
  const fromArrayOfObj = (arr) => {
    if (!Array.isArray(arr)) return ''
    for (const it of arr) {
      if (typeof it?.url === 'string') return toHttps(it.url)
      if (typeof it?.Url === 'string') return toHttps(it.Url)
      if (typeof it?.src === 'string') return toHttps(it.src)
      if (typeof it?.link === 'string') return toHttps(it.link)
    }
    return ''
  }
  const s2 =
    fromArrayOfObj(p.ImageUrl) ||
    fromArrayOfObj(p.images) ||
    fromArrayOfObj(p.photos)
  if (s2) return s2

  // 4) Objets imbriqués
  if (typeof p?.mainImage?.url === 'string') return toHttps(p.mainImage.url)
  if (typeof p?.picture?.url === 'string') return toHttps(p.picture.url)
  if (typeof p?.thumbnail?.url === 'string') return toHttps(p.thumbnail.url)

  // 5) Rien trouvé
  return ''
}

/* ----------------- Normalisation produit ----------------- */
function normalizeProduct(raw = {}) {
  // titre : on essaie de prendre Name (comme "IGLOO 48QT LAGUMA- WHITE")
  const title =
    raw.Name ||
    raw.name ||
    raw.title ||
    raw.Title ||
    'Produit'

  const imageUrl = pickImageUrl(raw)

  // Prix prioritaire: Stores.Cassablanca.SalePrice
  const cassablanca = raw?.Stores?.Cassablanca?.SalePrice
  const price =
    typeof cassablanca === 'number' && !Number.isNaN(cassablanca)
      ? cassablanca
      : raw.NewSalePrice ??
        raw.NewPrice ??
        raw.price ??
        0

  return {
    ...raw,
    title,
    imageUrl,
    price,
  }
}

/* ----------------- Construction de requêtes (filtres) ----------------- */
/**
 * Essaye de construire une requête paginée avec filtre d'égalité.
 * - Supporte subId (sous-catégorie) OU catId (catégorie) — priorité à subId.
 * - Essaie plusieurs champs courants dans tes docs.
 */
function buildFilteredQuery(base, { subId, catId, pageSize, cursor }) {
  const common = [orderBy('__name__'), qLimit(pageSize)]
  const withCursor = (qr) =>
    cursor ? query(qr, startAfter(cursor)) : qr

  // Sous-catégorie en priorité
  if (subId) {
    const subFields = [
      'SubCategorie.Id',
      'SubCategorie.ID',
      'SubCategorie.id',
      'SubCategory.Id',
      'SubCategory.ID',
      'SubCategory.id',
      'subCategorieId',
      'subCategoryId',
    ]
    for (const f of subFields) {
      try {
        const qr = query(base, where(f, '==', subId), ...common)
        return withCursor(qr)
      } catch (e) {
        // si le champ est invalide pour Firestore -> on essaie le suivant
        continue
      }
    }
  }

  // Catégorie (si pas de subId)
  if (catId) {
    const catFields = [
      'Categorie.Id',
      'Categorie.ID',
      'Categorie.id',
      'Category.Id',
      'Category.ID',
      'Category.id',
      'categoryId',
    ]
    for (const f of catFields) {
      try {
        const qr = query(base, where(f, '==', catId), ...common)
        return withCursor(qr)
      } catch (e) {
        continue
      }
    }
  }

  // Pas de filtre valide → requête basique (tri sur __name__)
  const qr = query(base, orderBy('__name__'), qLimit(pageSize))
  return withCursor(qr)
}

/* ----------------- API: Pagination (avec filtres) ----------------- */
/**
 * fetchProductsPage({ pageSize, cursor, subId, catId })
 * - pageSize: nombre d'items
 * - cursor: dernier DocumentSnapshot de la page précédente
 * - subId / catId: filtres d'égalité (un seul appliqué, subId prioritaire)
 */
// export function buildFilteredQuery1(base, { subId, catId, pageSize, cursor, validationStatus }) {
//   let q = query(
//     base,
//     where("validationStatus", "==", validationStatus || "validated"),
//     orderBy("createdAt", "desc"),
//     limit(pageSize)
//   );

//   if (catId) q = query(q, where("catId", "==", catId));
//   if (subId) q = query(q, where("subId", "==", subId));

//   if (cursor) q = query(q, startAfter(cursor));

//   return q;
// }
export async function fetchProductsPage({
  pageSize = 24,
  cursor = null,
  subId = "",
  catId = "",
} = {}) {
  const base = collection(db, "productsShop");

  // ✅ buildFilteredQuery doit intégrer where('validationStatus','==','validated')
  const qr = buildFilteredQuery1(base, {
    subId,
    catId,
    pageSize,
    cursor,
    validationStatus: "validated",
  });

  const snap = await getDocs(qr);

  const docs = snap.docs.map((d) => ({
    id: d.id,
    ...normalizeProduct(d.data()),
  }));

  if (docs.length) {
    const noImg = docs.filter((x) => !x.imageUrl).slice(0, 3);
    if (noImg.length) {
      console.warn(
        "[GoShop] Produits sans image (exemples):",
        noImg.map((x) => ({ id: x.id, sample: x })).slice(0, 3)
      );
    }
  }

  const nextCursor = snap.docs.length ? snap.docs[snap.docs.length - 1] : null;
  return { items: docs, nextCursor, hasMore: Boolean(nextCursor) };
}
// export function buildFilteredQuery1(
//   baseCol,
//   { subId = "", catId = "", pageSize = 24, cursor = null, validationStatus = "validated" } = {}
// ) {
//   const constraints = [];

//   // ✅ uniquement produits validés
//   constraints.push(where("validationStatus", "==", validationStatus));

//   // ✅ filtres catégorie / sous-catégorie
//   // ⚠️ adapte les champs selon ton modèle réel
//   if (catId) constraints.push(where("Categorie.Id", "==", String(catId)));
//   if (subId) constraints.push(where("Categorie.SubCategorie.Id", "==", String(subId)));

//   // ✅ tri stable pour pagination (par défaut __name__)
//   constraints.push(orderBy("__name__"));

//   // ✅ pagination
//   if (cursor) constraints.push(startAfter(cursor));

//   // ✅ page size
//   constraints.push(limit(pageSize));

//   return query(baseCol, ...constraints);
// }
export function buildFilteredQuery1(
  baseCol,
  { subId = "", catId = "", pageSize = 24, cursor = null, validationStatus = "validated" } = {}
) {
  const constraints = [];

  constraints.push(where("validationStatus", "==", validationStatus));

  if (catId) constraints.push(where("Categorie.Id", "==", String(catId)));
  if (subId) constraints.push(where("Categorie.SubCategorie.Id", "==", String(subId)));

  constraints.push(orderBy("__name__"));

  if (cursor) constraints.push(startAfter(cursor));

  constraints.push(qLimit(pageSize)); // ✅ OK maintenant

  return query(baseCol, ...constraints);
}
/* ----------------- API: Tout (sans pagination) ----------------- */
// export async function fetchProductsAll() {
//   const snap = await getDocs(collection(db, 'productsShop'))
//   return snap.docs.map((d) => ({
//     id: d.id,
//     ...normalizeProduct(d.data()),
//   }))
// }
export async function fetchProductsAll() {
  const qy = query(
    collection(db, "productsShop"),
    where("validationStatus", "==", "validated")
  );

  const snap = await getDocs(qy);

  return snap.docs.map((d) => ({
    id: d.id,
    ...normalizeProduct(d.data()),
  }));
}
/* ----------------- API: Un produit ----------------- */
// export async function fetchProduct(pid) {
//   const r = doc(db, 'productsShop', pid)
//   const s = await getDoc(r)
//   return s.exists()
//     ? { id: s.id, ...normalizeProduct(s.data()) }
//     : null
// }
// export async function fetchProduct(pid) { try { const r = doc(db, "productsShop", pid); const s = await getDoc(r); return s.exists() ? { id: s.id, ...normalizeProduct(s.data()) } : null; } catch (e) { console.error("[fetchProduct] failed:", pid, e?.code, e?.message); return null; } }
export async function fetchProduct(pid) {
  try {
    const r = doc(db, "productsShop", pid);
    const s = await getDoc(r);

    if (!s.exists()) return null;

    const data = s.data() || {};

    // ✅ Critère: uniquement les produits validés
    if (data.validationStatus !== "validated") return null;

    return { id: s.id, ...normalizeProduct(data) };
  } catch (e) {
    console.error("[fetchProduct] failed:", pid, e?.code, e?.message);
    return null;
  }
}
/* ----------------- API: CRUD ----------------- */
export async function addProduct(data) {
  return await addDoc(collection(db, 'productsShop'), {
    ...data,
    createdAt: serverTimestamp(),
  })
}

export async function updateProduct(id, data) {
  const ref = doc(db, 'productsShop', id)
  return await updateDoc(ref, data)
}

export async function deleteProduct(id) {
  const ref = doc(db, 'productsShop', id)
  return await deleteDoc(ref)
}
